// src/app/features/conversations/Components/MessageEditor/TextArea/index.tsx

import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  useCallback,
} from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import EmojiPicker from 'Components/EmojiPicker'; // Your custom picker

const trimmer = (str: string) =>
  str.replace(/^(<p><br><\/p>)+|(<p><br><\/p>)+$/g, '');

interface MessageEditorProps {
  value?: string;
  onChange?: (content: string) => void;
  onSubmit?: () => void;
  readOnly: boolean;
  disabled?: boolean;
}

const TextArea = forwardRef(
  (
    {
      value = '',
      onChange = () => {},
      onSubmit = () => {},
      readOnly = false,
    }: MessageEditorProps,
    ref,
  ) => {
    const quillRef = useRef<ReactQuill>(null);
    const [content, setContent] = useState(value);
    const [trimmedContent, setTrimmedContent] = useState('');

    useEffect(() => {
      setContent(value);
      quillRef.current?.focus();
    }, [value]);

    useEffect(() => {
      setTrimmedContent(trimmer(content));
    }, [content]);

    useEffect(() => {
      onChange(trimmedContent);
    }, [trimmedContent, onChange]);

    useImperativeHandle(ref, () => ({
      getRawContent() {
        return quillRef.current?.getEditor().getText();
      },
    }));

    const isCursorInsideLink = () => {
      const editor = quillRef.current?.getEditor();
      if (!editor) return false;

      const selection = editor.getSelection();
      if (!selection) return false;

      const format = editor.getFormat(selection.index, selection.length);
      return format && format.link;
    };

    const insertEmoji = useCallback((emoji: string) => {
      const editor = quillRef.current?.getEditor();
      if (!editor) return;
      const range = editor.getSelection(true);
      if (!range) return;

      // Insert emoji at cursor
      editor.insertText(range.index, emoji, 'user');
      // Move cursor after the emoji
      editor.setSelection(range.index + emoji.length, 0);
    }, []);

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!quillRef.current) return;
      if (e.key === 'Enter' && !e.shiftKey) {
        if (isCursorInsideLink()) return;
        setContent(trimmedContent);
        onSubmit();
        e.preventDefault();
      }
    };

    return (
      <div className="relative">
        <ReactQuill
          ref={quillRef}
          value={content}
          onChange={setContent}
          readOnly={readOnly}
          className="message-editor max-w-full"
          theme="snow"
          onKeyDown={handleKeyDown}
          placeholder="Write a message here..."
          modules={{
            toolbar: [
              ['bold', 'italic', 'underline'],
              [{ list: 'ordered' }, { list: 'bullet' }],
              [
                { align: '' },
                { align: 'center' },
                { align: 'right' },
                { align: 'justify' },
              ],
              [{ indent: '-1' }, { indent: '+1' }],
              ['link'],
              ['clean'],
            ],
          }}
          formats={[
            'bold',
            'italic',
            'underline',
            'list',
            'bullet',
            'align',
            'link',
            'header',
            'indent',
          ]}
        />

        {/* 
          Position the emoji trigger on the right; 
          Your EmojiPicker can be either a button or pop-up. 
          If your custom <EmojiPicker> already has a pop-up/overlay, 
          you only need to position the trigger (e.g., a button that says "Emoji +").
        */}
        <div
          className="
            absolute 
            right-2
            bottom-2
            flex 
            items-center 
            space-x-2
            z-10
          "
        >
          <EmojiPicker onChange={insertEmoji} />
        </div>
      </div>
    );
  },
);

export default TextArea;
